import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import BlockIcon from "@material-ui/icons/Block";
import Checkbox from "@material-ui/core/Checkbox";
import {
  Grid,
  TablePagination,
  Box,
  Typography,
} from "@material-ui/core";
import { isArray } from "lodash";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import ContactListItemProModal from "../../components/ContactListItemProModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_DATA") {
    const data = action.payload;
    const newData = [];
    if (isArray(data)) {
      data.forEach((item) => {
        const index = state.findIndex((u) => u.id === item.id);
        if (index !== -1) {
          state[index] = item;
        } else {
          newData.push(item);
        }
      });
    }
    return [...state, ...newData];
  }
  if (action.type === "UPDATE_DATA") {
    const item = action.payload;
    const index = state.findIndex((u) => u.id === item.id);
    if (index !== -1) {
      state[index] = item;
      return [...state];
    } else {
      return [item, ...state];
    }
  }
  if (action.type === "DELETE_DATA") {
    const id = action.payload;
    const index = state.findIndex((u) => u.id === id);
    if (index !== -1) {
      state.splice(index, 1);
    }
    return [...state];
  }
  if (action.type === "RESET") {
    return [];
  }
  return state;
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  headerCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
  },
  tableHeader: {
    fontWeight: "bold",
    backgroundColor: theme.palette.grey[100],
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
  variableChip: {
    margin: theme.spacing(0.25),
    fontSize: "0.7rem",
    height: 20,
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
}));

const ContactListProItems = () => {
  const classes = useStyles();
  const history = useHistory();
  const { contactListProId } = useParams();
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState(null); // null | 'imported' | 'duplicate' | 'invalid'
  const [contactList, setContactList] = useState(null);

  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [addContactModalOpen, setAddContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [loadingIds, setLoadingIds] = useState(false);

  // Carregar dados da lista
  useEffect(() => {
    const fetchContactList = async () => {
      try {
        const { data } = await api.get(`/contact-lists-pro/${contactListProId}`);
        setContactList(data);
      } catch (err) {
        toastError(err);
        history.push("/campaigns-pro");
      }
    };
    fetchContactList();
  }, [contactListProId, history]);

  // Reset ao mudar filtros
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setSelectedIds(new Set());
  }, [searchParam, pageSize, statusFilter]);

  // Carregar contatos
  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, pageSize, contactListProId, statusFilter]);

  // Socket listeners
  useEffect(() => {
    const companyId = user.companyId;

    const onContactListItemPro = (data) => {
      if (data.action === "delete") {
        dispatch({ type: "DELETE_DATA", payload: +data.id });
        setTotalCount((prev) => Math.max(0, prev - 1));
      }
      if (data.action === "create") {
        dispatch({ type: "UPDATE_DATA", payload: data.record });
        setTotalCount((prev) => prev + 1);
      }
      if (data.action === "update") {
        dispatch({ type: "UPDATE_DATA", payload: data.record });
      }
    };

    socket.on(`company-${companyId}-ContactListItemPro`, onContactListItemPro);

    return () => {
      socket.off(`company-${companyId}-ContactListItemPro`, onContactListItemPro);
    };
  }, [user, socket]);

  const fetchContacts = async () => {
    try {
      const params = { searchParam, pageNumber, pageSize };
      if (statusFilter) params.statusFilter = statusFilter;
      const { data } = await api.get(`/contact-lists-pro/${contactListProId}/items`, { params });

      if (pageNumber === 1) {
        dispatch({ type: "RESET" });
      }
      dispatch({ type: "LOAD_DATA", payload: data.records });
      setTotalCount(data.count || 0);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleStatusFilterClick = (filter) => {
    setStatusFilter((prev) => (prev === filter ? null : filter));
  };

  const handleSelectAllPage = (event) => {
    if (event.target.checked) {
      setSelectedIds(new Set(contacts.map((c) => c.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAllFiltered = async () => {
    if (!statusFilter) return;
    setLoadingIds(true);
    try {
      const { data } = await api.get(`/contact-lists-pro/${contactListProId}/items/ids`, {
        params: { statusFilter },
      });
      setSelectedIds(new Set(data.ids || []));
      toast.success(`${(data.ids || []).length} contatos selecionados.`);
    } catch (err) {
      toastError(err);
    }
    setLoadingIds(false);
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      await api.post(`/contact-lists-pro/${contactListProId}/items/bulk-delete`, { ids });
      toast.success(`${ids.length} contato(s) excluído(s).`);
      setSelectedIds(new Set());
      setBulkDeleteModalOpen(false);
      setPageNumber(1);
      fetchContacts();
      if (contactList) {
        const { data } = await api.get(`/contact-lists-pro/${contactListProId}`);
        setContactList(data);
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handlePageChange = (event, newPage) => {
    setPageNumber(newPage + 1);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPageNumber(1);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contact-lists-pro/${contactListProId}/items/${contactId}`);
      toast.success("Contato excluído!");
      dispatch({ type: "DELETE_DATA", payload: contactId });
      setTotalCount((prev) => prev - 1);
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
  };

  const renderVariables = (contact) => {
    const vars = [];
    if (contact.var1) vars.push({ key: "var1", value: contact.var1 });
    if (contact.var2) vars.push({ key: "var2", value: contact.var2 });
    if (contact.var3) vars.push({ key: "var3", value: contact.var3 });
    if (contact.var4) vars.push({ key: "var4", value: contact.var4 });
    if (contact.var5) vars.push({ key: "var5", value: contact.var5 });

    if (vars.length === 0) return <Typography variant="caption" color="textSecondary">—</Typography>;

    return (
      <Box>
        {vars.map((v) => (
          <Tooltip key={v.key} title={`${v.key}: ${v.value}`}>
            <Chip
              label={`${v.key}: ${v.value.substring(0, 15)}${v.value.length > 15 ? "..." : ""}`}
              size="small"
              className={classes.variableChip}
              variant="outlined"
            />
          </Tooltip>
        ))}
      </Box>
    );
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingContact && `Excluir contato "${deletingContact.name || deletingContact.number}"?`}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteContact(deletingContact.id)}
      >
        Esta ação não pode ser desfeita.
      </ConfirmationModal>

      <ConfirmationModal
        title={`Excluir ${selectedIds.size} contato(s) selecionado(s)?`}
        open={bulkDeleteModalOpen}
        onClose={() => setBulkDeleteModalOpen(false)}
        onConfirm={handleBulkDelete}
      >
        Esta ação não pode ser desfeita.
      </ConfirmationModal>

      <ContactListItemProModal
        open={addContactModalOpen}
        onClose={() => {
          setAddContactModalOpen(false);
          setEditingContact(null);
        }}
        contactListProId={contactListProId}
        contact={editingContact}
        onSuccess={() => {
          setEditingContact(null);
          fetchContacts();
        }}
      />

      <MainHeader>
        <Box display="flex" alignItems="center">
          <IconButton
            className={classes.backButton}
            onClick={() => history.push("/campaigns-pro")}
          >
            <ArrowBackIcon />
          </IconButton>
          <Title>
            📋 Contatos: {contactList?.name || "Carregando..."}
          </Title>
        </Box>
      </MainHeader>

      <Paper className={classes.headerCard}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              placeholder="Buscar por nome ou número..."
              type="search"
              value={searchParam}
              onChange={handleSearch}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box display="flex" gap={2} justifyContent="flex-end" alignItems="center" flexWrap="wrap">
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                setEditingContact(null);
                setAddContactModalOpen(true);
              }}
                size="small"
              >
                Adicionar Contato
              </Button>
              {selectedIds.size > 0 && (
                <>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => setBulkDeleteModalOpen(true)}
                    startIcon={<DeleteOutlineIcon />}
                  >
                    Excluir selecionados ({selectedIds.size})
                  </Button>
                </>
              )}
              <Chip
                label={`Total: ${totalCount} contatos`}
                color="primary"
                variant={statusFilter === null ? "default" : "outlined"}
                onClick={() => statusFilter != null && setStatusFilter(null)}
                style={{ cursor: statusFilter != null ? "pointer" : "default" }}
              />
              {contactList && (
                <>
                  <Chip
                    label={`Importados: ${contactList.totalImported || 0}`}
                    style={{
                      backgroundColor: statusFilter === "imported" ? "#2e7d32" : "#4caf50",
                      color: "white",
                      cursor: "pointer",
                      border: statusFilter === "imported" ? "2px solid #1b5e20" : "none",
                    }}
                    onClick={() => handleStatusFilterClick("imported")}
                  />
                  <Chip
                    label={`Duplicados: ${contactList.totalDuplicates || 0}`}
                    style={{
                      backgroundColor: statusFilter === "duplicate" ? "#e65100" : "#ff9800",
                      color: "white",
                      cursor: "pointer",
                      border: statusFilter === "duplicate" ? "2px solid #bf360c" : "none",
                    }}
                    onClick={() => handleStatusFilterClick("duplicate")}
                  />
                  <Chip
                    label={`Inválidos: ${contactList.totalInvalid || 0}`}
                    style={{
                      backgroundColor: statusFilter === "invalid" ? "#b71c1c" : "#f44336",
                      color: "white",
                      cursor: "pointer",
                      border: statusFilter === "invalid" ? "2px solid #8b0000" : "none",
                    }}
                    onClick={() => handleStatusFilterClick("invalid")}
                  />
                </>
              )}
              {statusFilter && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={loadingIds || totalCount === 0}
                  onClick={handleSelectAllFiltered}
                >
                  {loadingIds ? "Carregando..." : `Selecionar todos (${totalCount})`}
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader} padding="checkbox">
                <Checkbox
                  indeterminate={selectedIds.size > 0 && selectedIds.size < contacts.length}
                  checked={contacts.length > 0 && selectedIds.size === contacts.length}
                  onChange={handleSelectAllPage}
                  disabled={loading || contacts.length === 0}
                />
              </TableCell>
              <TableCell className={classes.tableHeader} align="center" style={{ width: 48 }}></TableCell>
              <TableCell className={classes.tableHeader}>Nome</TableCell>
              <TableCell className={classes.tableHeader}>Número</TableCell>
              <TableCell className={classes.tableHeader}>Variáveis</TableCell>
              <TableCell className={classes.tableHeader}>Tags</TableCell>
              <TableCell className={classes.tableHeader} align="center">Opt-in</TableCell>
              <TableCell className={classes.tableHeader} align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((contact) => (
              <TableRow key={contact.id} selected={selectedIds.has(contact.id)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedIds.has(contact.id)}
                    onChange={() => handleSelectOne(contact.id)}
                    disabled={loading}
                  />
                </TableCell>
                <TableCell align="center" style={{ width: 48 }}>
                  <Tooltip title={contact.isWhatsappValid ? "WhatsApp válido" : "WhatsApp inválido"}>
                    <IconButton size="small" disableRipple>
                      {contact.isWhatsappValid ? (
                        <CheckCircleIcon htmlColor="#4caf50" fontSize="small" />
                      ) : (
                        <BlockIcon htmlColor="#9e9e9e" fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell>{contact.name || <em style={{ color: "#999" }}>Sem nome</em>}</TableCell>
                <TableCell>
                  <Typography variant="body2" style={{ fontFamily: "monospace" }}>
                    {contact.number}
                  </Typography>
                </TableCell>
                <TableCell>{renderVariables(contact)}</TableCell>
                <TableCell>
                  {contact.tags ? (
                    <Chip label={contact.tags} size="small" />
                  ) : (
                    <Typography variant="caption" color="textSecondary">—</Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={contact.optedIn ? "Sim" : "Não"}
                    size="small"
                    color={contact.optedIn ? "primary" : "default"}
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Editar">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingContact(contact);
                        setAddContactModalOpen(true);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setConfirmModalOpen(true);
                        setDeletingContact(contact);
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={8} />}
            {!loading && contacts.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className={classes.emptyState}>
                  <Typography variant="body1">
                    Nenhum contato encontrado.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <Box display="flex" justifyContent="flex-end" p={2}>
          <TablePagination
            component="div"
            count={totalCount}
            page={pageNumber - 1}
            onPageChange={handlePageChange}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handlePageSizeChange}
            rowsPerPageOptions={[10, 20, 50, 100]}
            labelRowsPerPage="Por página:"
          />
        </Box>
      </Paper>
    </MainContainer>
  );
};

export default ContactListProItems;

